import React, { useEffect, useMemo, useRef, useState } from 'react';
import { C, F, T, Btn, Pill, Card, CardHead } from '../theme/uiKit';
import { getLeadTimeService } from '../services';
import type { LeadTimeItem, LeadTimeHistoryEntry } from '../services/types';

// Supplier Gateway · Lead Time Update.
// Editable: lead time, end of sale, end of life, hazard class, DG goods.
// Read-only: country code. Edits are keyed by item id so they persist across
// pagination; changes are reviewed before saving. Per-item audit history opens
// in a right-side drawer. Item data comes from the lead-time service (empty
// until the backend is wired).

type Edit = {
    leadTime?: number | null;
    endOfSale?: string;
    endOfLife?: string;
    hazardClass?: string;
    dgGoods?: boolean;
};

const HAZARD_CLASSES = [
    'None',
    'Class 1',
    'Class 2',
    'Class 3',
    'Class 4',
    'Class 5',
    'Class 6',
    'Class 7',
    'Class 8',
    'Class 9',
];

const PAGE_SIZE = 25;

const CSV_HEADERS = [
    'Item',
    'Description',
    'LeadTimeDays',
    'EndOfSale',
    'EndOfLife',
    'HazardClass',
    'DGGoods',
    'Country',
];

const csvEscape = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

function normalizeDate(v: string): string {
    const s = v.trim();
    if (s === '') return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mdy) {
        const [, m, d, y] = mdy;
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    return s;
}

function downloadCsv(filename: string, content: string) {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

// Minimal CSV parser (handles quoted fields and commas within quotes).
function parseCsv(text: string): string[][] {
    const rows: string[][] = [];
    let field = '';
    let row: string[] = [];
    let inQuotes = false;
    for (let i = 0; i < text.length; i += 1) {
        const ch = text[i];
        if (inQuotes) {
            if (ch === '"') {
                if (text[i + 1] === '"') {
                    field += '"';
                    i += 1;
                } else inQuotes = false;
            } else field += ch;
        } else if (ch === '"') inQuotes = true;
        else if (ch === ',') {
            row.push(field);
            field = '';
        } else if (ch === '\n' || ch === '\r') {
            if (ch === '\r' && text[i + 1] === '\n') i += 1;
            row.push(field);
            field = '';
            if (row.some((c) => c !== '')) rows.push(row);
            row = [];
        } else field += ch;
    }
    if (field !== '' || row.length) {
        row.push(field);
        if (row.some((c) => c !== '')) rows.push(row);
    }
    return rows;
}

type UploadResult = {
    fileName: string;
    changed: number;
    unknown: number;
    info?: string;
    errors: string[];
};

// ── Audit history drawer ──────────────────────────────────────────────────────
function HistoryDrawer({ item, onClose }: { item: string; onClose: () => void }) {
    const [rows, setRows] = useState<LeadTimeHistoryEntry[]>([]);

    useEffect(() => {
        let live = true;
        getLeadTimeService()
            .getHistory(item)
            .then((h) => {
                if (live) setRows(h);
            })
            .catch(() => {
                if (live) setRows([]);
            });
        return () => {
            live = false;
        };
    }, [item]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const cellVal = (v: string) =>
        v ? (
            <strong style={{ color: C.primary }}>{v}</strong>
        ) : (
            <span style={{ color: C.textMuted }}>—</span>
        );
    const hcell: React.CSSProperties = { padding: '9px 12px', whiteSpace: 'nowrap', fontSize: T.body };

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 20 }}>
            <button
                type="button"
                aria-label="Close audit history"
                onClick={onClose}
                style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(0,0,0,0.35)',
                    border: 'none',
                    padding: 0,
                    cursor: 'default',
                }}
            />
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Audit history"
                style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: 720,
                    maxWidth: '100%',
                    background: C.surface,
                    boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
                    display: 'flex',
                    flexDirection: 'column',
                    fontFamily: F.ui,
                }}
            >
                <div
                    style={{
                        padding: '16px 20px',
                        borderBottom: `1px solid ${C.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <div>
                        <div style={{ fontSize: T.section, fontWeight: 700, color: C.text }}>
                            Audit history
                        </div>
                        <div style={{ fontSize: T.caption, color: C.textMuted, fontFamily: F.mono }}>
                            {item}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        style={{
                            background: 'transparent',
                            border: 'none',
                            fontSize: 22,
                            cursor: 'pointer',
                            color: C.textMuted,
                        }}
                    >
                        ×
                    </button>
                </div>
                <div style={{ flex: 1, overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: C.bg, textAlign: 'left', color: C.textSub }}>
                                {['Date', 'Lead time', 'End of Sale', 'End of Life', 'Hazard class', 'Changed by'].map(
                                    (h) => (
                                        <th key={h} style={{ ...hcell, fontWeight: 600 }}>
                                            {h}
                                        </th>
                                    ),
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={6}
                                        style={{ padding: 24, textAlign: 'center', color: C.textMuted }}
                                    >
                                        No history for this item.
                                    </td>
                                </tr>
                            ) : (
                                rows.map((h) => (
                                    <tr key={h.when} style={{ borderTop: `1px solid ${C.borderLight}` }}>
                                        <td style={{ ...hcell, color: C.textSub, fontFamily: F.mono }}>
                                            {h.when}
                                        </td>
                                        <td style={hcell}>{cellVal(h.leadTime)}</td>
                                        <td style={hcell}>{cellVal(h.eos)}</td>
                                        <td style={hcell}>{cellVal(h.eol)}</td>
                                        <td style={hcell}>{cellVal(h.hazard)}</td>
                                        <td style={{ ...hcell, color: C.textMuted }}>{h.by}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

// ── Main screen ───────────────────────────────────────────────────────────────
const cell: React.CSSProperties = { padding: '9px 10px', whiteSpace: 'nowrap' };
const inputStyle: React.CSSProperties = {
    height: 32,
    border: `1px solid ${C.border}`,
    borderRadius: 5,
    padding: '0 8px',
    fontSize: T.body,
    fontFamily: F.ui,
    boxSizing: 'border-box',
};

function Title() {
    return (
        <>
            <h1 style={{ margin: '0 0 4px', fontSize: T.title, fontWeight: 700, color: C.text }}>
                Lead Time Update
            </h1>
            <p style={{ margin: '0 0 20px', fontSize: T.body, color: C.textSub }}>
                Update supplier item attributes. Country code is read-only. Edit across pages —
                changes are reviewed before saving.
            </p>
        </>
    );
}

export default function LeadTimeScreen() {
    const [rows, setRows] = useState<LeadTimeItem[]>([]);
    const [edits, setEdits] = useState<Record<string, Edit>>({});
    const [page, setPage] = useState(1);
    const [showChangedOnly, setShowChangedOnly] = useState(false);
    const [stage, setStage] = useState<'edit' | 'review' | 'saved'>('edit');
    const [historyItem, setHistoryItem] = useState<string | null>(null);
    const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Load the supplier item catalog from the service (empty until wired).
    useEffect(() => {
        let live = true;
        getLeadTimeService()
            .listItems()
            .then((items) => {
                if (live) setRows(items);
            })
            .catch(() => {
                if (live) setRows([]);
            });
        return () => {
            live = false;
        };
    }, []);

    const isChanged = (r: LeadTimeItem) => {
        const e = edits[r.item];
        if (!e) return false;
        return (
            (e.leadTime !== undefined && e.leadTime !== null && e.leadTime !== r.current) ||
            (e.endOfSale !== undefined && e.endOfSale !== r.endOfSale) ||
            (e.endOfLife !== undefined && e.endOfLife !== r.endOfLife) ||
            (e.hazardClass !== undefined && e.hazardClass !== r.hazardClass) ||
            (e.dgGoods !== undefined && e.dgGoods !== r.dgGoods)
        );
    };

    const changedRows = useMemo(() => rows.filter(isChanged), [rows, edits]);
    const visibleRows = showChangedOnly ? changedRows : rows;
    const pageCount = Math.max(1, Math.ceil(visibleRows.length / PAGE_SIZE));
    const safePage = Math.min(page, pageCount);
    const pageRows = visibleRows.slice((safePage - 1) * PAGE_SIZE, (safePage - 1) * PAGE_SIZE + PAGE_SIZE);

    const set = (item: string, patch: Edit) =>
        setEdits((prev) => ({ ...prev, [item]: { ...prev[item], ...patch } }));
    const leadOf = (r: LeadTimeItem) => {
        const v = edits[r.item]?.leadTime;
        return v === undefined || v === null ? '' : String(v);
    };
    const eosOf = (r: LeadTimeItem) => edits[r.item]?.endOfSale ?? r.endOfSale;
    const eolOf = (r: LeadTimeItem) => edits[r.item]?.endOfLife ?? r.endOfLife;
    const hazOf = (r: LeadTimeItem) => edits[r.item]?.hazardClass ?? r.hazardClass;
    const dgOf = (r: LeadTimeItem) => edits[r.item]?.dgGoods ?? r.dgGoods;

    const reset = () => {
        setEdits({});
        setPage(1);
        setShowChangedOnly(false);
        setStage('edit');
        setUploadResult(null);
    };

    const handleDownload = () => {
        const lines = [CSV_HEADERS.join(',')];
        rows.forEach((r) => {
            lines.push(
                [
                    r.item,
                    r.desc,
                    String(edits[r.item]?.leadTime ?? r.current),
                    eosOf(r),
                    eolOf(r),
                    hazOf(r),
                    dgOf(r) ? 'Yes' : 'No',
                    r.country,
                ]
                    .map((c) => csvEscape(String(c)))
                    .join(','),
            );
        });
        downloadCsv('lead-time-template.csv', lines.join('\n'));
    };

    const handleFile = (file: File) => {
        const reader = new FileReader();
        reader.onload = () => {
            const parsed = parseCsv(String(reader.result));
            if (parsed.length < 2) {
                setUploadResult({
                    fileName: file.name,
                    changed: 0,
                    unknown: 0,
                    errors: ['File is empty or has no data rows.'],
                });
                return;
            }
            const header = parsed[0].map((h) => h.trim());
            const idx = (name: string) => header.indexOf(name);
            const iItem = idx('Item');
            const iLead = idx('LeadTimeDays');
            const iEos = idx('EndOfSale');
            const iEol = idx('EndOfLife');
            const iHaz = idx('HazardClass');
            if (iItem < 0) {
                setUploadResult({
                    fileName: file.name,
                    changed: 0,
                    unknown: 0,
                    errors: ["Missing required 'Item' column."],
                });
                return;
            }

            const provided: string[] = [];
            if (iLead >= 0) provided.push('Lead time');
            if (iEos >= 0) provided.push('End of Sale');
            if (iEol >= 0) provided.push('End of Life');
            if (iHaz >= 0) provided.push('Hazard Class');

            const byId = new Map(rows.map((r) => [r.item, r]));
            const next: Record<string, Edit> = { ...edits };
            const errors: string[] = [];
            let changed = 0;
            let unknown = 0;

            for (let i = 1; i < parsed.length; i += 1) {
                const cells = parsed[i];
                const id = (cells[iItem] ?? '').trim();
                const r = id ? byId.get(id) : undefined;
                if (id && !r) {
                    unknown += 1;
                } else if (r) {
                    const patch: Edit = {};
                    if (iLead >= 0 && (cells[iLead] ?? '').trim() !== '') {
                        const n = Number(cells[iLead].trim());
                        if (Number.isNaN(n)) errors.push(`${id}: lead time is not a number`);
                        else if (n !== r.current) patch.leadTime = n;
                    }
                    if (iEos >= 0) {
                        const v = normalizeDate((cells[iEos] ?? '').trim());
                        if (v !== r.endOfSale) patch.endOfSale = v;
                    }
                    if (iEol >= 0) {
                        const v = normalizeDate((cells[iEol] ?? '').trim());
                        if (v !== r.endOfLife) patch.endOfLife = v;
                    }
                    if (iHaz >= 0) {
                        const v = (cells[iHaz] ?? '').trim();
                        if (v !== '' && v !== r.hazardClass) patch.hazardClass = v;
                    }
                    if (Object.keys(patch).length) {
                        next[id] = { ...next[id], ...patch };
                        changed += 1;
                    }
                }
            }
            const info = provided.length
                ? `Columns processed from file: ${provided.join(', ')}.`
                : undefined;
            setEdits(next);
            setUploadResult({ fileName: file.name, changed, unknown, info, errors });
            setShowChangedOnly(true);
            setPage(1);
        };
        reader.readAsText(file);
    };

    const saveAll = () => {
        const changes = changedRows.map((r) => ({ item: r.item, ...edits[r.item] }));
        getLeadTimeService()
            .saveChanges(changes)
            .finally(() => setStage('saved'));
    };

    // Saved confirmation.
    if (stage === 'saved') {
        return (
            <div style={{ fontFamily: F.ui, color: C.text }}>
                <Title />
                <Card style={{ padding: 40, textAlign: 'center' }}>
                    <div style={{ marginBottom: 10 }}>
                        <Pill label="Saved" color="green" dot />
                    </div>
                    <h2 style={{ margin: 0, fontSize: T.section, fontWeight: 700, color: C.text }}>
                        Supplier Item Attributes updated
                    </h2>
                    <p style={{ margin: '8px 0 16px', fontSize: T.body, color: C.textSub }}>
                        {changedRows.length} item(s) submitted for review.
                    </p>
                    <Btn label="Done" variant="sec" onClick={reset} />
                </Card>
            </div>
        );
    }

    // Review before save.
    if (stage === 'review') {
        const diffCell = (isDiff: boolean, oldV: string, newV: string) =>
            isDiff ? (
                <span>
                    <span style={{ color: C.textMuted, textDecoration: 'line-through' }}>
                        {oldV || '—'}
                    </span>
                    {' → '}
                    <strong style={{ color: C.primary }}>{newV || '—'}</strong>
                </span>
            ) : (
                <span style={{ color: C.textMuted }}>—</span>
            );
        return (
            <div style={{ fontFamily: F.ui, color: C.text }}>
                <Title />
                <Card>
                    <CardHead
                        title={`Review ${changedRows.length} change(s)`}
                        right={<Pill label="Confirm before saving" color="amber" dot />}
                    />
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: T.body }}>
                            <thead>
                                <tr style={{ background: C.bg, textAlign: 'left', color: C.textSub }}>
                                    {['Item', 'Country', 'Description', 'Lead time', 'End of Sale', 'End of Life', 'Hazard Class'].map(
                                        (h) => (
                                            <th key={h} style={{ ...cell, fontWeight: 600 }}>
                                                {h}
                                            </th>
                                        ),
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {changedRows.map((r) => {
                                    const e = edits[r.item];
                                    const ltChanged =
                                        e.leadTime !== undefined &&
                                        e.leadTime !== null &&
                                        e.leadTime !== r.current;
                                    const eosChanged =
                                        e.endOfSale !== undefined && e.endOfSale !== r.endOfSale;
                                    const eolChanged =
                                        e.endOfLife !== undefined && e.endOfLife !== r.endOfLife;
                                    const hazChanged =
                                        e.hazardClass !== undefined && e.hazardClass !== r.hazardClass;
                                    return (
                                        <tr key={r.item} style={{ borderTop: `1px solid ${C.borderLight}` }}>
                                            <td style={{ ...cell, fontFamily: F.mono }}>{r.item}</td>
                                            <td style={{ ...cell, color: C.textMuted }}>{r.country}</td>
                                            <td style={{ ...cell, whiteSpace: 'normal', minWidth: 180, color: C.textSub }}>
                                                {r.desc}
                                            </td>
                                            <td style={cell}>
                                                {diffCell(ltChanged, String(r.current), String(e.leadTime))}
                                            </td>
                                            <td style={cell}>
                                                {diffCell(eosChanged, r.endOfSale, e.endOfSale ?? '')}
                                            </td>
                                            <td style={cell}>
                                                {diffCell(eolChanged, r.endOfLife, e.endOfLife ?? '')}
                                            </td>
                                            <td style={cell}>
                                                {diffCell(hazChanged, r.hazardClass, e.hazardClass ?? '')}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: 14,
                            borderTop: `1px solid ${C.borderLight}`,
                        }}
                    >
                        <Btn label="Back to editing" variant="ghost" onClick={() => setStage('edit')} />
                        <Btn
                            label={`Save all ${changedRows.length} change(s)`}
                            variant="pri"
                            onClick={saveAll}
                        />
                    </div>
                </Card>
            </div>
        );
    }

    // Edit stage.
    return (
        <div style={{ fontFamily: F.ui, color: C.text, paddingBottom: changedRows.length ? 76 : 0 }}>
            {historyItem ? (
                <HistoryDrawer item={historyItem} onClose={() => setHistoryItem(null)} />
            ) : null}

            <Title />

            {/* Bulk update via file */}
            <Card style={{ marginBottom: 16 }}>
                <CardHead title="Bulk update via file" />
                <div style={{ display: 'flex', gap: 20, padding: 18, flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 260px' }}>
                        <div style={{ fontSize: T.body, fontWeight: 600, color: C.text, marginBottom: 4 }}>
                            1. Download template
                        </div>
                        <p style={{ margin: '0 0 12px', fontSize: T.caption, color: C.textSub }}>
                            A CSV of your items with current values. Opens in Excel — edit and
                            re-upload. Only the columns present in your file are updated.
                        </p>
                        <Btn label="⤓ Download template (CSV)" variant="sec" onClick={handleDownload} />
                    </div>
                    <div style={{ flex: '1 1 320px' }}>
                        <div style={{ fontSize: T.body, fontWeight: 600, color: C.text, marginBottom: 4 }}>
                            2. Upload edited file
                        </div>
                        <label
                            htmlFor="leadtime-file-input"
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                                e.preventDefault();
                                const f = e.dataTransfer.files?.[0];
                                if (f) handleFile(f);
                            }}
                            style={{
                                display: 'block',
                                border: `2px dashed ${C.border}`,
                                borderRadius: 8,
                                padding: '18px 16px',
                                textAlign: 'center',
                                cursor: 'pointer',
                                background: C.bg,
                            }}
                        >
                            <div style={{ fontSize: 22, marginBottom: 4 }}>⬆</div>
                            <div style={{ fontSize: T.body, color: C.text }}>
                                Drop CSV here or <span style={{ color: C.primary }}>browse</span>
                            </div>
                            <input
                                id="leadtime-file-input"
                                ref={fileInputRef}
                                type="file"
                                accept=".csv,text/csv"
                                style={{ display: 'none' }}
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (f) handleFile(f);
                                    if (fileInputRef.current) fileInputRef.current.value = '';
                                }}
                            />
                        </label>
                    </div>
                </div>
                {uploadResult ? (
                    <div
                        style={{
                            borderTop: `1px solid ${C.borderLight}`,
                            padding: 16,
                            background: uploadResult.errors.length ? C.amberSoft : C.greenSoft,
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <Pill
                                label={uploadResult.errors.length ? 'Imported with warnings' : 'Imported'}
                                color={uploadResult.errors.length ? 'amber' : 'green'}
                                dot
                            />
                            <span style={{ fontSize: T.caption, color: C.textSub, fontFamily: F.mono }}>
                                {uploadResult.fileName}
                            </span>
                        </div>
                        <div style={{ fontSize: T.body, color: C.text }}>
                            <strong>{uploadResult.changed}</strong> record(s) changed and staged below
                            {uploadResult.unknown > 0 ? (
                                <>
                                    {' · '}
                                    <strong>{uploadResult.unknown}</strong> unknown item(s) skipped
                                </>
                            ) : null}
                        </div>
                        {uploadResult.info ? (
                            <div style={{ fontSize: T.caption, color: C.textSub, marginTop: 4 }}>
                                {uploadResult.info}
                            </div>
                        ) : null}
                        {uploadResult.errors.length > 0 ? (
                            <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: T.caption, color: C.amber }}>
                                {uploadResult.errors.slice(0, 6).map((e) => (
                                    <li key={e}>{e}</li>
                                ))}
                                {uploadResult.errors.length > 6 ? (
                                    <li>…and {uploadResult.errors.length - 6} more</li>
                                ) : null}
                            </ul>
                        ) : null}
                    </div>
                ) : null}
            </Card>

            {/* Editable item attributes table */}
            <Card>
                <CardHead
                    title="Supplier Item Attributes"
                    right={
                        <label
                            htmlFor="leadtime-changed-only"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                fontSize: T.body,
                                color: C.textSub,
                                cursor: 'pointer',
                            }}
                        >
                            <input
                                id="leadtime-changed-only"
                                type="checkbox"
                                checked={showChangedOnly}
                                onChange={(e) => {
                                    setShowChangedOnly(e.target.checked);
                                    setPage(1);
                                }}
                            />
                            Show changed only
                        </label>
                    }
                />
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: T.body }}>
                        <thead>
                            <tr style={{ background: C.bg, textAlign: 'left', color: C.textSub }}>
                                {['Item', 'Description', 'Lead time (days)', 'End of Sale', 'End of Life', 'Hazard Class', 'DG Goods', 'Country', 'History'].map(
                                    (h) => (
                                        <th key={h} style={{ ...cell, fontWeight: 600 }}>
                                            {h}
                                        </th>
                                    ),
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {pageRows.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={9}
                                        style={{ padding: 30, textAlign: 'center', color: C.textMuted }}
                                    >
                                        {rows.length === 0
                                            ? 'No items to show yet.'
                                            : 'No changed items yet.'}
                                    </td>
                                </tr>
                            ) : (
                                pageRows.map((r) => {
                                    const changed = isChanged(r);
                                    return (
                                        <tr
                                            key={r.item}
                                            style={{
                                                borderTop: `1px solid ${C.borderLight}`,
                                                background: changed ? C.primarySoft : undefined,
                                            }}
                                        >
                                            <td style={{ ...cell, fontFamily: F.mono }}>
                                                {changed ? (
                                                    <span style={{ color: C.primary, marginRight: 6 }}>*</span>
                                                ) : null}
                                                {r.item}
                                            </td>
                                            <td style={{ ...cell, whiteSpace: 'normal', minWidth: 260, color: C.textSub }}>
                                                {r.desc}
                                            </td>
                                            <td style={cell}>
                                                <input
                                                    type="number"
                                                    placeholder={String(r.current)}
                                                    value={leadOf(r)}
                                                    onChange={(e) =>
                                                        set(r.item, {
                                                            leadTime:
                                                                e.target.value === ''
                                                                    ? null
                                                                    : Number(e.target.value),
                                                        })
                                                    }
                                                    style={{ ...inputStyle, width: 64, fontFamily: F.mono }}
                                                />
                                            </td>
                                            <td style={cell}>
                                                <input
                                                    type="date"
                                                    value={eosOf(r)}
                                                    onChange={(e) => set(r.item, { endOfSale: e.target.value })}
                                                    style={{ ...inputStyle, width: 140 }}
                                                />
                                            </td>
                                            <td style={cell}>
                                                <input
                                                    type="date"
                                                    value={eolOf(r)}
                                                    onChange={(e) => set(r.item, { endOfLife: e.target.value })}
                                                    style={{ ...inputStyle, width: 140 }}
                                                />
                                            </td>
                                            <td style={cell}>
                                                <select
                                                    value={hazOf(r)}
                                                    onChange={(e) => set(r.item, { hazardClass: e.target.value })}
                                                    style={{ ...inputStyle, width: 100 }}
                                                >
                                                    {HAZARD_CLASSES.map((h) => (
                                                        <option key={h} value={h}>
                                                            {h}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td style={cell}>
                                                <button
                                                    type="button"
                                                    onClick={() => set(r.item, { dgGoods: !dgOf(r) })}
                                                    title="Click to toggle"
                                                    style={{
                                                        background: 'transparent',
                                                        border: 'none',
                                                        padding: 0,
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    <Pill
                                                        label={dgOf(r) ? 'Yes' : 'No'}
                                                        color={dgOf(r) ? 'red' : 'green'}
                                                        dot
                                                    />
                                                </button>
                                            </td>
                                            <td style={{ ...cell, color: C.textMuted }}>{r.country}</td>
                                            <td style={cell}>
                                                <Btn
                                                    label="History"
                                                    variant="sec"
                                                    size="xs"
                                                    onClick={() => setHistoryItem(r.item)}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 14px',
                        borderTop: `1px solid ${C.borderLight}`,
                    }}
                >
                    <span style={{ fontSize: T.caption, color: C.textMuted }}>
                        Showing {visibleRows.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1}-
                        {Math.min(safePage * PAGE_SIZE, visibleRows.length)} of {visibleRows.length}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Btn
                            label="Prev"
                            variant="sec"
                            size="xs"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                        />
                        <span style={{ fontSize: T.caption, color: C.textSub }}>
                            Page {safePage} of {pageCount}
                        </span>
                        <Btn
                            label="Next"
                            variant="sec"
                            size="xs"
                            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                        />
                    </div>
                </div>
            </Card>

            {/* Unsaved-changes bar */}
            {changedRows.length > 0 ? (
                <div
                    style={{
                        position: 'fixed',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: C.nav,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '14px 28px',
                        gap: 16,
                        zIndex: 10,
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: T.section, fontWeight: 700 }}>{changedRows.length}</span>
                        <span style={{ fontSize: T.body, color: C.navText }}>
                            item(s) with unsaved changes across all pages
                        </span>
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <button
                            type="button"
                            onClick={reset}
                            style={{
                                background: 'transparent',
                                color: C.navText,
                                border: '1px solid rgba(255,255,255,0.2)',
                                borderRadius: 5,
                                padding: '8px 16px',
                                fontSize: T.body,
                                cursor: 'pointer',
                                fontFamily: F.ui,
                            }}
                        >
                            Discard all
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setShowChangedOnly(true);
                                setPage(1);
                            }}
                            style={{
                                background: 'transparent',
                                color: '#fff',
                                border: '1px solid rgba(255,255,255,0.35)',
                                borderRadius: 5,
                                padding: '8px 16px',
                                fontSize: T.body,
                                cursor: 'pointer',
                                fontFamily: F.ui,
                            }}
                        >
                            Show changed
                        </button>
                        <button
                            type="button"
                            onClick={() => setStage('review')}
                            style={{
                                background: C.navActive,
                                color: '#fff',
                                border: 'none',
                                borderRadius: 5,
                                padding: '8px 18px',
                                fontSize: T.body,
                                fontWeight: 600,
                                cursor: 'pointer',
                                fontFamily: F.ui,
                            }}
                        >
                            Review &amp; save
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
